import { useEffect, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Porque é que uma urgência saiu da fila sem ninguém a atender.
 *
 * ─── PORQUE SE PERGUNTA ─────────────────────────────────────────────────────
 *
 * Sem o motivo, «Dispensada» no relatório não se interpreta: 20 dispensas por
 * engano são um problema de formação, 20 por o doente se ter ido embora são um
 * problema de capacidade — e os dois eram o mesmo número. O «Foi-se embora» é
 * o que vale a pena: é o único que mede doentes perdidos.
 *
 * ─── UM TOQUE, NÃO UMA FRASE ────────────────────────────────────────────────
 *
 * Quem dispensa está ao balcão ou entre doentes. Se fosse texto livre vinha
 * vazio, e aí o motivo não acrescentava nada. A nota só aparece no «Outro»,
 * para não convidar a escrever onde o toque já diz tudo.
 *
 * ─── PARTILHADO PELOS QUATRO SÍTIOS ─────────────────────────────────────────
 *
 * A faixa do topo, as duas dashboards e o «Cancelar Urgência» da ficha do
 * cliente. É o mesmo ato feito de sítios diferentes, e quatro cópias deste
 * ecrã divergiam na primeira alteração.
 */

/* Tem de bater com `MOTIVOS_DISPENSA` em `models/urgencia.mjs`. Se divergir, o
   servidor recusa com «Motivo de dispensa «X» não existe» — falha alto, que é
   o que se quer de uma lista repetida. */
const MOTIVOS = [
  "Foi-se embora",
  "Já foi atendido",
  "Remarcou",
  "Engano",
  "Outro",
];

function ModalDispensarUrgencia({
  aberto,
  fechar,
  nomeCliente,
  /* `aoConfirmar({ motivoDispensa, notaDispensa })` */
  aoConfirmar,
  aEnviar = false,
}) {
  const [motivo, setMotivo] = useState("");
  const [nota, setNota] = useState("");

  /* Reabrir não deve trazer a escolha da vez anterior — a urgência é outra. */
  useEffect(() => {
    if (aberto) {
      setMotivo("");
      setNota("");
    }
  }, [aberto]);

  const podeConfirmar =
    !!motivo && (motivo !== "Outro" || nota.trim().length > 0) && !aEnviar;

  return (
    <Dialog open={aberto} onClose={fechar} maxWidth="xs" fullWidth>
      <DialogTitle>Dispensar urgência</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {nomeCliente
            ? `${nomeCliente} sai da fila sem ser atendido. A equipa deixa de ser avisada.`
            : "A urgência sai da fila sem ser atendida."}
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Porquê?
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {MOTIVOS.map((m) => (
            <Chip
              key={m}
              label={m}
              onClick={() => setMotivo(m)}
              color={motivo === m ? "error" : "default"}
              variant={motivo === m ? "filled" : "outlined"}
            />
          ))}
        </Box>

        {motivo === "Outro" && (
          <TextField
            label="O que aconteceu"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            inputProps={{ maxLength: 200 }}
            helperText={`${nota.length}/200`}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={fechar} disabled={aEnviar}>
          Cancelar
        </Button>
        <Button
          onClick={() =>
            aoConfirmar({
              motivoDispensa: motivo,
              notaDispensa: motivo === "Outro" ? nota.trim() : undefined,
            })
          }
          variant="contained"
          color="error"
          disabled={!podeConfirmar}
        >
          {aEnviar ? "A dispensar…" : "Dispensar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModalDispensarUrgencia;
